import QQC, { FLEX_OPTION_MAX_AGE } from './qqc-module';
import animateNumber from './plugins/animateNumber';
import { debounce, throttle, resizeTable } from './utils/utils';
import { slideToggle } from './startup/slide';
import { getLeadDimensions } from './utils/ga4-tracking/custom-dimensions';
import { pushToDataLayer } from './utils/ga4-tracking/push-to-data-layer';
import { getYobFromAge } from './utils/date';

//aliases
const doc = document;
const win = window;
const mediaMatcher = window.matchMedia('(min-width: 768px)');

//helper functions
const animatePriceField = (field, fieldValue, decimals = true) => {
  const decimalNumber = decimals ? 2 : 0;
  const previousFieldValue = parseFloat(field.innerHTML.replace('€', '').replace(',', '.').trim());

  let fromValue;

  if (isNaN(previousFieldValue) || field.innerHTML === '-') {
    fromValue = parseFloat(fieldValue) + Math.floor(Math.random() * 31) + 50;
  } else {
    fromValue = parseFloat(previousFieldValue.toFixed(decimalNumber));
  }

  animateNumber(
    value => {
      if (document.documentElement.lang === 'en') {
        field.innerHTML = `&euro;${value.toFixed(decimalNumber)}`;
      } else {
        field.innerHTML = `${value.toFixed(decimalNumber).replace('.', ',')} &euro;`;
      }
    },
    fromValue,
    fieldValue,
    550
  );
};

const scrollToPrice = priceFields => {
  let alreadyScrolled = false;
  priceFields.forEach(elem => {
    const stickyTab = [...document.querySelectorAll('.stickyNavigationTabsStart-block')].find(block => block.contains(elem));

    // block might be in a stickyNav tab ->
    // switch tabs so the block is actually visible and can be scrolled to
    if (stickyTab && window.setStickyNavigationTab) {
      window.setStickyNavigationTab(`#${stickyTab.id}`, null, false);
    }

    if (elem.offsetParent && !alreadyScrolled) {
      alreadyScrolled = true;
      if (!isElementInViewport(elem) && elem.getBoundingClientRect().top > 0) {
        win.scrollTo({
          top: elem.getBoundingClientRect().top - doc.body.getBoundingClientRect().top - Math.max(doc.documentElement.clientHeight || 0, window.innerHeight || 0) / 2,
          behavior: 'smooth'
        });
      }
    }
  });
};

function isElementInViewport(el) {
  const rect = el.getBoundingClientRect();
  return rect.height > 0 && rect.top >= 0 && rect.bottom <= (win.innerHeight || doc.documentElement.clientHeight);
}

let trackingEventFired = false;
const fireQuickQuoteTrackingEvent = (tracking, node, age) => {
  if (!trackingEventFired) {
    let contentSection = node.closest('section');
    let section = contentSection ? contentSection.classList[0] : 'na';

    setTimeout(() => {
      pushToDataLayer({
        event: 'quick_quote_calculated',
        element_section: section,
        yob: getYobFromAge(age),
        job_status: 'na',
        ...getLeadDimensions({}, { includeProduct: true }),
        ...(tracking === 'clinic' && { insurance_category: 'clinic' }),
        ...(tracking === 'clinic' && { insurance_group: 'clinic' })
      });
    }, 1500);
    trackingEventFired = true;
    //timeout to prevent multiple tracking events for different inputs and "fast clicking" users
    setTimeout(() => {
      trackingEventFired = false;
    }, 3500);
  }
};

let qqc;
const initQQC = () => {
  qqc = craftConfig.global.qqcAge ? QQC(craftConfig.global.qqcAge) : QQC();
  qqc.subscribe(
    throttle(() => {
      win.dispatchEvent(new Event('qqc-calculated'));
    }, 300)
  );
};

/******************************************************************/
/* ----- QQC TARIFFS BLOCKS ----- */

doc.querySelectorAll('.qqcTariffs-block').forEach(block => {
  //references
  const calculationSlider = block.querySelector('.calculate');
  const tariffWrapper = block.querySelector('.tariffs');
  const mobileNav = tariffWrapper.querySelector('nav');
  const tariffBoxes = tariffWrapper.querySelectorAll('.list .box');

  if (!qqc) {
    initQQC();
  }

  //slider and slider controls
  const slider = calculationSlider.querySelector('.slider');
  const controlLeft = slider.querySelector('.slider-control.left');
  const controlRight = slider.querySelector('.slider-control.right');
  const rangeSlider = slider.querySelector('input');

  // age input
  const ageInputForm = block.querySelector('form[data-qqc]');
  const tracking = ageInputForm.dataset.tracking;
  const ageInput = ageInputForm.querySelector('.age-input input');

  //prevent double initialization
  ageInputForm.isInitialised = true;

  rangeSlider.min = qqc.getMinAge();
  rangeSlider.max = qqc.getMaxAge();

  // link input values to qqc age
  if (controlLeft && controlRight) {
    controlLeft.addEventListener('click', () => qqc.setAge(qqc.getAge() - 1));
    controlRight.addEventListener('click', () => {
      qqc.setAge(qqc.getAge() + 1);
    });
  }
  ageInput.addEventListener('input', () => {
    if (ageInput.value) {
      qqc.setAge(ageInput.value);
    }
  });
  ageInput.addEventListener('blur', () => {
    if (!ageInput.value) {
      qqc.setAge();
    }
  });
  rangeSlider.addEventListener('input', () => {
    qqc.setAge(rangeSlider.value);
  });
  const updateInputs = () => {
    const newAge = qqc.getAge();
    ageInput.value = newAge;
    rangeSlider.value = newAge;
    rangeSlider.dispatchEvent(new Event('update'));
    if (controlLeft && controlRight) {
      controlLeft.disabled = rangeSlider.value <= rangeSlider.min;
      controlRight.disabled = rangeSlider.value >= rangeSlider.max;
    }
  };
  qqc.subscribe(() => {
    updateInputs();
  });
  updateInputs();

  //toogle isSticky class
  const checkTop = () => {
    const rect = mobileNav.getBoundingClientRect();
    mobileNav.classList.toggle('isSticky', rect.top <= 64);
  };
  win.addEventListener('customLoaded', checkTop);
  win.addEventListener('scroll', throttle(checkTop, 300, true, true));
  win.addEventListener('resize', debounce(checkTop, 300));

  //tab specific code
  tariffBoxes.forEach(async box => {
    const priceElem = box.querySelector('.price strong');
    const tariffType = priceElem.dataset.qqcPrice;
    const tariffName = priceElem.dataset.type;
    const mobilePriceElem = mobileNav.querySelector(`[data-type="${tariffName}"]`);

    //initialize the tariff
    const tariff = await qqc.addTariff(tariffType, tariffName);

    // handle price fields, animate them the first time they scroll into view
    const updatePrice = () => {
      [priceElem, mobilePriceElem].forEach(field => animatePriceField(field, tariff.getPremium() / 100));
    };
    const scrollHandler = throttle(() => {
      if (isElementInViewport(priceElem) || isElementInViewport(mobilePriceElem)) {
        win.removeEventListener('scroll', scrollHandler);
        tariff.subscribe(() => {
          updatePrice();
        });
        updatePrice();
      }
    }, 300);
    win.addEventListener('scroll', scrollHandler);
    scrollHandler();

    // mobile tab switching
    let activeNav;
    mobileNav.querySelectorAll('li').forEach(li => {
      const tariffKey = li.dataset.tariff;
      if (li.classList.contains('active')) {
        activeNav = li;
      }
      li.addEventListener('click', e => {
        if (activeNav === li) {
          return;
        }
        activeNav.classList.remove('active');
        li.classList.add('active');
        activeNav = li;
        tariffBoxes.forEach(box => {
          box.classList.toggle('active', box.dataset.tariffTab === tariffKey);
        });
      });
    });

    const setOption = (option, value) => {
      qqc.setTariffOption(tariffType, tariffName, option, value);
    };

    //handle full tariff name animations
    const nameEndingsContainer = box.querySelector('.name-endings');
    if (nameEndingsContainer) {
      const name1 = nameEndingsContainer.querySelector('.name-1');
      const name2 = nameEndingsContainer.querySelector('.name-2');
      const name3 = nameEndingsContainer.querySelector('.name-3');

      const updateTariffNames = () => {
        name1.classList.toggle('active', !tariff.getOption('bonus'));
        name2.classList.toggle('active', tariff.getOption('bonus'));
        name3.classList.toggle('active', tariff.getOption('care'));
      };
      tariff.subscribe(updateTariffNames);
      updateTariffNames();
    }

    //handle care switch
    const careRadio = box.querySelector('.extension-1');
    let careValue = '';
    if (careRadio) {
      const careOff = careRadio.querySelector('input[value="1"]');
      const careOn = careRadio.querySelector('input[value="2"]');
      careValue = careOff.nextElementSibling.innerText.split(' ')[0];
      careOff.addEventListener('click', () => {
        setOption('care', false);
        careValue = careOff.nextElementSibling.innerText.split(' ')[0];
      });
      careOn.addEventListener('click', () => {
        setOption('care', true);
        careValue = careOn.nextElementSibling.innerText.split(' ')[0];
      });
      tariff.subscribe(() => {
        const isCare = tariff.getOption('care');
        careOn.checked = isCare;
        careOff.checked = !isCare;
      });
    }

    //handle bonus switch
    const bonusRadio = box.querySelector('.extension-2');
    let bonusValue = '';
    if (bonusRadio) {
      const bonusOff = bonusRadio.querySelector('input[value="1"]');
      const bonusOn = bonusRadio.querySelector('input[value="2"]');
      //if the off option doesn't exist it's just gonna be set to on and no click events are needed
      if (bonusOff) {
        bonusValue = bonusOff.nextElementSibling.innerText.split(' %')[0];
        bonusOff.addEventListener('click', () => {
          setOption('bonus', false);
          bonusValue = bonusOff.nextElementSibling.innerText.split(' %')[0];
        });
        bonusOn.addEventListener('click', () => {
          setOption('bonus', true);
          bonusValue = bonusOn.nextElementSibling.innerText.split(' %')[0];
        });
        tariff.subscribe(() => {
          const isBonus = tariff.getOption('bonus');
          bonusOn.checked = isBonus;
          bonusOff.checked = !isBonus;
        });
      } else {
        bonusValue = bonusOn.nextElementSibling.innerText.split(' %')[0];
      }
    }

    //handle higlight box resizing
    if (box.classList.contains('highlight')) {
      const highlightBox = box.querySelector('.highlight-content');
      const highlightHeight = () => {
        const highlightBoxHeight = highlightBox.offsetHeight;
        const root = document.documentElement;
        root.style.setProperty('--highlightHeight', highlightBoxHeight + 20 + 'px');
      };

      highlightHeight();
      win.addEventListener('resize', debounce(highlightHeight, 300));
      doc.addEventListener('DOMContentLoaded', highlightHeight);
    }

    // update link names
    box.querySelectorAll('.buttons a').forEach(link => {
      if (!link.href) {
        return;
      }

      const url = new URL(link.href);
      const updateUrlParams = () => {
        url.searchParams.set('tariff-id', tariff.getName());
        link.href = url.toString();
        const dataset = link.dataset;
        dataset.trYob = getYobFromAge(qqc.getAge());
        dataset.trTariff = tariff.getName();
        dataset.trTeeth_cleaning = careValue.replace('€', '');
        dataset.trDenture = bonusValue.replace('%', '');
      };
      tariff.subscribe(updateUrlParams);
      updateUrlParams();
    });
    //fire tracking event on tariff changes
    tariff.subscribe(() => fireQuickQuoteTrackingEvent(tracking, box, qqc.getAge()));
  });
  block.querySelectorAll('.note-content').forEach(note => {
    const toggle = note.querySelector('.toggle');
    const contentToggle = note.querySelector('.content-toggle');
    toggle.addEventListener('click', () => {
      note.classList.toggle('active');
      slideToggle(contentToggle);
    });
  });
});

/******************************************************************/
/* ----- QQC TARIFFS V2 BLOCKS ----- */

doc.querySelectorAll('.qqcTariffsV2-block').forEach(block => {
  //references
  const tariffWrapper = block.querySelector('.tariffs');
  const mobileNav = tariffWrapper.querySelector('nav');
  const tariffBoxes = tariffWrapper.querySelectorAll('.list .box');

  if (!qqc) {
    initQQC();
  }

  // age dropdown
  const ageDropdownForm = block.querySelector('form[data-qqc]');
  const tracking = ageDropdownForm.dataset.tracking;
  const ageDropdown = ageDropdownForm.querySelector('.age-dropdown select');

  //prevent double initialization
  ageDropdownForm.isInitialised = true;

  // link Dropdown values to qqc age
  ageDropdown.addEventListener('input', () => {
    if (ageDropdown.value) {
      qqc.setAge(ageDropdown.value);
    }
  });
  ageDropdown.addEventListener('blur', () => {
    if (!ageDropdown.value) {
      qqc.setAge();
    }
  });
  const updateInputs = () => {
    const newAge = qqc.getAge();
    ageDropdown.value = newAge;
  };
  qqc.subscribe(() => {
    updateInputs();
  });
  updateInputs();

  //toogle isSticky class
  const checkTop = () => {
    const rect = mobileNav.getBoundingClientRect();
    mobileNav.classList.toggle('isSticky', rect.top <= 64);
  };
  win.addEventListener('customLoaded', checkTop);
  win.addEventListener('scroll', throttle(checkTop, 300, true, true));
  win.addEventListener('resize', debounce(checkTop, 300));

  //tab specific code
  tariffBoxes.forEach(async box => {
    const priceElem = box.querySelector('[data-qqc-price]');
    const tariffType = priceElem.dataset.qqcPrice;
    const tariffName = priceElem.dataset.type;
    const mobilePriceElem = mobileNav.querySelector(`[data-type="${tariffName}"]`);
    const extraPayment = box.querySelectorAll('.extra-payment');
    const notificationContainer = box.querySelector('.notification-container');

    //initialize the tariff
    const tariff = await qqc.addTariff(tariffType, tariffName);

    // handle price fields, animate them the first time they scroll into view
    const updatePrice = () => {
      [priceElem, mobilePriceElem].forEach(field => animatePriceField(field, tariff.getPremium() / 100));
    };
    const updateExtraPayment = () => {
      extraPayment.forEach(field => animatePriceField(field, (tariff.getOptionPremium(field.dataset.tariffOption) / 100) * 12));
    };
    const scrollHandler = throttle(() => {
      if (isElementInViewport(priceElem) || isElementInViewport(mobilePriceElem)) {
        win.removeEventListener('scroll', scrollHandler);
        tariff.subscribe(() => {
          updatePrice();
          updateExtraPayment();
        });
        updatePrice();
        updateExtraPayment();
      }
    }, 300);
    win.addEventListener('scroll', scrollHandler);
    scrollHandler();

    document.addEventListener('updateActiveItem', e => {
      const cardClicked = e.detail.tariffName.replace('-v2', '');
      mobileNav.querySelectorAll('li').forEach(li => {
        const isActive = li.dataset.tariff === cardClicked;
        li.classList.toggle('active', isActive);
        if (isActive) activeNav = li;
        tariffBoxes.forEach(box => box.classList.toggle('active', box.dataset.tariffTab === cardClicked));
      });
    });

    // mobile tab switching
    let activeNav;
    mobileNav.querySelectorAll('li').forEach(li => {
      const tariffKey = li.dataset.tariff;
      if (li.classList.contains('active')) activeNav = li;
      li.addEventListener('click', e => {
        if (activeNav === li) return;
        activeNav.classList.remove('active');
        li.classList.add('active');
        activeNav = li;
        tariffBoxes.forEach(box => box.classList.toggle('active', box.dataset.tariffTab === tariffKey));
      });
    });

    const setOption = (option, value) => {
      qqc.setTariffOption(tariffType, tariffName, option, value);
    };

    //handle full tariff name animations
    const nameEndingsContainer = box.querySelector('.name-endings');
    if (nameEndingsContainer) {
      const name1 = nameEndingsContainer.querySelector('.name-1');
      const name2 = nameEndingsContainer.querySelector('.name-2');
      const name3 = nameEndingsContainer.querySelector('.name-3');

      const updateTariffNames = () => {
        name1.classList.toggle('active', !tariff.getOption('bonus'));
        name2.classList.toggle('active', tariff.getOption('bonus'));
        name3.classList.toggle('active', tariff.getOption('care'));
        name1.ariaHidden = tariff.getOption('bonus');
        name2.ariaHidden = !tariff.getOption('bonus');
        name3.ariaHidden = !tariff.getOption('care');
      };
      tariff.subscribe(updateTariffNames);
      updateTariffNames();
    }

    //handle care switch
    const careRadio = box.querySelector('.extension-1');
    if (careRadio) {
      const careOff = careRadio.querySelector('input[value="1"]');
      const careOn = careRadio.querySelector('input[value="2"]');
      const coverageAmount = careRadio.querySelector('.coverage-amount');

      careOff.addEventListener('click', () => {
        setOption('care', false);
      });
      careOn.addEventListener('click', () => {
        setOption('care', true);
      });
      tariff.subscribe(() => {
        notificationContainer.textContent = en ? `Tariff available from: €${tariff.getPremium() / 100}` : `Tarif verfügbar ab: ${(tariff.getPremium() / 100).toFixed(2).replace('.', ',')} €`;
        const isCare = tariff.getOption('care');
        careOn.checked = isCare;
        careOff.checked = !isCare;
        if (coverageAmount) {
          if (tariffName === 'business-v2') {
            animatePriceField(coverageAmount, isCare ? 100 : 70, false);
          } else {
            animatePriceField(coverageAmount, isCare ? 110 : 90, false);
          }
        }
      });
    }

    //handle bonus switch
    const bonusRadio = box.querySelector('.extension-2');
    if (bonusRadio) {
      const bonusOff = bonusRadio.querySelector('input[value="1"]');
      const bonusOn = bonusRadio.querySelector('input[value="2"]');
      //if the off option doesn't exist it's just gonna be set to on and no click events are needed
      if (bonusOff) {
        bonusOff.addEventListener('click', () => {
          setOption('bonus', false);
        });
        bonusOn.addEventListener('click', () => {
          setOption('bonus', true);
        });
        tariff.subscribe(() => {
          const isBonus = tariff.getOption('bonus');
          bonusOn.checked = isBonus;
          bonusOff.checked = !isBonus;
        });
      }
    }

    //handle higlight box resizing
    if (box.classList.contains('highlight')) {
      const highlightBox = box.querySelector('.highlight-content');
      const highlightHeight = () => {
        const highlightBoxHeight = highlightBox.offsetHeight;
        const root = document.documentElement;
        root.style.setProperty('--highlightHeight', highlightBoxHeight + 20 + 'px');
      };

      highlightHeight();
      win.addEventListener('resize', debounce(highlightHeight, 300));
      doc.addEventListener('DOMContentLoaded', highlightHeight);
    }

    // update link names
    box.querySelectorAll('.buttons a').forEach(link => {
      const url = new URL(link.href);
      const updateUrlParams = () => {
        url.searchParams.set('tariff-id', tariff.getName());
        link.href = url.toString();
      };
      tariff.subscribe(updateUrlParams);
      updateUrlParams();
    });

    //fire tracking event on tariff changes
    tariff.subscribe(() => fireQuickQuoteTrackingEvent(tracking, box, qqc.getAge()));
  });
  block.querySelectorAll('.note-content').forEach(note => {
    const toggle = note.querySelector('.toggle');
    const contentToggle = note.querySelector('.content-toggle');
    toggle.addEventListener('click', () => {
      note.classList.toggle('active');
      slideToggle(contentToggle);
    });
  });

  block.querySelectorAll('.box').forEach(box => {
    const scrollToLink = box.querySelector('.call-to-action-mobile > a');
    scrollToLink.addEventListener('click', () => {
      if (mediaMatcher.matches) {
        return;
      }
      const dentalPreSelection = new CustomEvent('dentalPreSelection', { detail: { tariff: box.dataset.tariffTab } });
      document.dispatchEvent(dentalPreSelection);
    });
  });
});

/******************************************************************/
/* ----- TARIFF TABLE BLOCK ----- */

doc
  .querySelectorAll('.tariffTable-block, .elab-tariffTable-block, .benefits-table-24.dental-benefits, .benefits-table-24.dental-mdm, .clinic-highlight-table .clinic-highlight-block')
  .forEach(block => {
    const flexOptions = block.querySelectorAll('.flex-option');
    const qqcPriceFields = block.querySelectorAll('[data-qqc-price]');
    const qqcOptions = block.querySelectorAll('[data-qqc-option]');
    const qqcButtons = block.querySelectorAll('.qqc-cta');
    const qqcButtonStandalone = block.querySelectorAll('[data-qqc-cta]');
    const qqcTariffNames = block.querySelectorAll('[data-qqc-dynamic-name]');
    const qqcTariffShows = block.querySelectorAll('[data-qqc-show]');
    const tariffSelectionCards = block.querySelector('.tariff-selection-cards');
    let benefitTableSelectionCards;
    if (block.classList.contains('benefits-table-24') && tariffSelectionCards) {
      benefitTableSelectionCards = tariffSelectionCards.querySelectorAll('label > input');
    }

    if ((qqcPriceFields.length || qqcOptions.length) && !qqc) {
      initQQC();
    }

    qqcButtons.forEach(async button => {
      const type = button.dataset.type;
      const tariffName = button.dataset.trTariff;
      const tariff = await qqc.addTariff(type, tariffName);
    });

    //price Fields
    qqcPriceFields.forEach(async priceField => {
      const type = priceField.dataset.qqcPrice;
      const tariffName = priceField.dataset.type;
      const tariff = await qqc.addTariff(type, tariffName);

      //price should be animated the first time it is scrolled into view. Event listener is throttled for performance
      const updatePrice = () => {
        animatePriceField(priceField, tariff.getPremium() / 100);
      };

      const scrollHandler = throttle(() => {
        if (isElementInViewport(priceField)) {
          win.removeEventListener('scroll', scrollHandler);
          tariff.subscribe(updatePrice);
          updatePrice();
        }
      }, 300);
      win.addEventListener('scroll', scrollHandler);
      scrollHandler();

      //flex option init
      if (tariffName === 'tuc-zweibett') {
        flexOptions.forEach(flexOption => {
          const flexContainer = flexOption.closest('tr') || flexOption;
          const flexInputs = flexOption.querySelectorAll('input');
          const table = flexContainer !== flexOption ? flexOption.closest('table') : false;
          flexInputs.forEach(flexInput => {
            flexInput.addEventListener('change', () => {
              const isSet = flexInput.type === 'radio' ? flexInput.value === 'true' : flexInput.checked;
              qqc.setTariffOption(type, tariffName, 'flex', isSet);
            });
          });
          //hide flex for certain age range
          const updateFlexHidden = () => {
            flexContainer.classList.toggle('is-hidden', qqc.getAge() > FLEX_OPTION_MAX_AGE);
            if (table) {
              resizeTable(table);
            }
          };
          const updateFlexValue = () => {
            const isFlex = tariff.getOption('flex');
            flexInputs.forEach(flexInput => {
              if (flexInput.type === 'radio') {
                flexInput.checked = isFlex.toString() === flexInput.value;
              } else {
                flexInput.checked = isFlex;
              }
            });
          };
          tariff.subscribe(() => {
            updateFlexValue();
            fireQuickQuoteTrackingEvent('clinic', flexOption, qqc.getAge());
          });
          qqc.subscribe(debounce(updateFlexHidden, 300));
          updateFlexHidden();
          updateFlexValue();
        });
      }
    });

    qqcOptions.forEach(async qqcOption => {
      const type = qqcOption.dataset.qqcType;
      const tariffName = qqcOption.dataset.qqcTariff;
      const option = qqcOption.dataset.qqcOption;
      const tariff = await qqc.addTariff(type, tariffName);
      if (!tariff) {
        return;
      }
      //handle care switch
      const offInput = qqcOption.querySelector('input[value="1"]');
      const onInput = qqcOption.querySelector('input[value="2"]');
      offInput?.addEventListener('click', () => {
        qqc.setTariffOption(type, tariffName, option, false);
        fireQuickQuoteTrackingEvent(type, qqcOption, qqc.getAge());
      });
      onInput?.addEventListener('click', () => {
        qqc.setTariffOption(type, tariffName, option, true);
        fireQuickQuoteTrackingEvent(type, qqcOption, qqc.getAge());
      });
      tariff.subscribe(() => {
        const isOption = tariff.getOption(option);
        onInput.checked = isOption;
        onInput.setAttribute('checked', isOption ? '' : null);
        offInput.checked = !isOption;
        offInput.setAttribute('checked', !isOption ? '' : null);
      });
    });

    qqcButtonStandalone.forEach(async button => {
      const type = button.dataset.qqcType;
      const tariffName = button.dataset.qqcTariff;
      const tariff = await qqc.addTariff(type, tariffName);
      if (!tariff) {
        return;
      }
      const handleTariffChanged = () => {
        const tariffName = tariff.getName();
        button.setAttribute('data-tr-tariff', tariffName);
        const url = new URL(button.href);
        url.searchParams.set('tariff-id', tariffName);
        button.href = url.toString();
      };
      tariff.subscribe(handleTariffChanged);
      handleTariffChanged();
    });

    qqcTariffNames.forEach(async name => {
      const type = name.dataset.qqcType;
      const tariffName = name.dataset.qqcTariff;
      const tariff = await qqc.addTariff(type, tariffName);
      if (!tariff) {
        return;
      }
      const nameFields = name.querySelectorAll('[data-qqc-name]');
      const setOptions = () => {
        nameFields.forEach(nameField => {
          let isShown;
          switch (nameField.dataset.qqcName) {
            case 'base':
              isShown = !tariff.getOption('bonus');
              break;
            case 'bonus':
              isShown = tariff.getOption('bonus');
              break;
            case 'care':
              isShown = tariff.getOption('care');
              break;
            default:
              isShown = false;
          }
          nameField.classList.toggle('is-hidden', !isShown);
        });
      };
      tariff.subscribe(setOptions);
      setOptions();
    });

    qqcTariffShows.forEach(async showField => {
      const type = showField.dataset.qqcTariff.includes('tud') ? 'dental' : 'clinic';
      const tariffName = showField.dataset.qqcTariff.replace('tud-', '');
      const tariff = await qqc.addTariff(type, tariffName);
      if (!tariff) {
        return;
      }
      const setHide = () => {
        showField.classList.toggle('is-hidden', !showField.dataset.qqcShow.includes(tariff.getName()));
      };
      tariff.subscribe(setHide);
      setHide();
    });

    if (benefitTableSelectionCards) {
      benefitTableSelectionCards.forEach(async card => {
        const type = 'dental';
        const tariffName = card.dataset.tariff.replace('tud-', '') + '-v2';
        const priceField = block.querySelector(`.dental-header [data-type=${tariffName}]`);

        const tariff = await qqc.addTariff(type, tariffName);
        if (!tariff) {
          return;
        }

        const switchHandler = () => {
          animatePriceField(priceField, tariff.getPremium() / 100);
          window.dispatchEvent(new Event('scroll'));
          card.removeEventListener('click', switchHandler);
        };

        const event = new CustomEvent('updateActiveItem', { detail: { tariffName: tariffName } });

        // Animate price when switching in benefit table
        card.addEventListener('click', () => {
          if (mediaMatcher.matches) {
            return;
          }
          switchHandler();
          document.dispatchEvent(event);
        });
      });
    }

    if (block.classList.contains('clinic-highlight-block')) {
      const initStickyObserver = tariffCard => {
        const mainHeader = tariffCard.querySelector('.main-header');
        const stickyHeader = tariffCard.querySelector('.sticky-header-wrapper');
        const additionalServices = tariffCard.querySelector('.additional.services');

        let headerIntersecting = false;
        let sentinelIntersecting = false;

        const observer = new IntersectionObserver(
          entries => {
            for (const entry of entries) {
              switch (entry.target) {
                case mainHeader:
                  headerIntersecting = entry.isIntersecting;
                  break;
                case additionalServices:
                  sentinelIntersecting = entry.isIntersecting;
                  break;
              }
            }
            stickyHeader.classList.toggle('show', !headerIntersecting && sentinelIntersecting);
          },
          {
            threshold: 0.5
          }
        );
        observer.observe(mainHeader);
        observer.observe(additionalServices);
      };

      const tariffCards = block.querySelectorAll('.tariff-card');
      tariffCards.forEach(card => {
        initStickyObserver(card);
      });
    }
  });

/******************************************************************/
/* ----- STATIC TARIFF TABLE CALCULATION ----- */

//animate static prices for tariff table
doc.querySelectorAll('.calc-price').forEach(elem => {
  //get initial price from html and an initial random value to count down from
  const calcPrice = elem.innerHTML.replace('€', '').replace(',', '.').trim();
  const calcPriceFrom = parseInt(calcPrice) + Math.floor(Math.random() * 31) + 50;
  //set the initial value for the animation in the html
  if (doc.documentElement.lang == 'en') {
    elem.innerHTML = `&euro; ${calcPriceFrom}`;
  } else {
    elem.innerHTML = `${calcPriceFrom} &euro;`;
  }

  //trigger the countdown animation on scoll into view
  const animateStaticPrice = throttle(() => {
    if (isElementInViewport(elem)) {
      win.removeEventListener('scroll', animateStaticPrice);
      animatePriceField(elem, calcPrice, false);
    }
  }, 300);
  win.addEventListener('scroll', animateStaticPrice);
  animateStaticPrice();
});

/******************************************************************/
/* ----- INDEPENDENT QQC AGE INPUTS ----- */

//qqc input fields
const ageInputForms = doc.querySelectorAll('form[data-qqc]');
if (ageInputForms.length && !qqc) {
  initQQC();
}
ageInputForms.forEach(form => {
  if (form.isInitialised) {
    return;
  }
  const qqcType = form.dataset.qqc;
  const tracking = form.dataset.tracking;

  const ageInput = form.querySelector('input');
  const priceFields = doc.querySelectorAll(`[data-qqc-price="${qqcType}"]`);

  ageInput.placeholder = qqc.getAge();

  const submitHandler = ev => {
    ev.preventDefault();
    qqc.setAge(ageInput.value);
    scrollToPrice(priceFields);

    // tracking events
    if (ageInput.value.length > 1) {
      let thisSection = form.closest('section');
      const pricenodes = thisSection.querySelectorAll('[data-qqc-price]');
      if (pricenodes.length === 0) {
        const nodeWithPrices = doc.querySelector('[data-qqc-price]');
        fireQuickQuoteTrackingEvent(tracking, nodeWithPrices, ageInput.value);
      } else {
        fireQuickQuoteTrackingEvent(tracking, form, ageInput.value);
      }
    }
  };
  if (form.hasAttribute('data-auto-submit')) {
    ageInput.addEventListener('input', ev => {
      if (ageInput.value) {
        submitHandler(ev);
      }
    });
    ageInput.addEventListener('blur', ev => {
      if (!ageInput.value) {
        submitHandler(ev);
      }
    });
  }
  form.addEventListener('submit', submitHandler);
  qqc.subscribe(() => (ageInput.value = qqc.getAge()));
});

/******************************************************************/
/* ----- QQC SCROLLTO FUNCTIONALITY  ----- */

// scrollto functionality
document.querySelectorAll('[data-scroll-to]').forEach(scrollTo => {
  scrollTo.addEventListener('click', ev => {
    ev.preventDefault();

    const header = document.getElementById('header');
    const headerHeight = header.offsetHeight;
    const entry = document.querySelector(scrollTo.dataset.scrollTo);

    if (entry) {
      window.scrollTo({
        top: entry.getBoundingClientRect().top - document.body.getBoundingClientRect().top - headerHeight - 16,
        behavior: 'smooth'
      });
      setTimeout(() => {
        entry.classList.add('highlight');
        setTimeout(() => entry.classList.remove('highlight'), 2500);
      }, 300);
    }
  });
});
